import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Loader2, Target, Trash2, MessageCircle, Download, Search,
  LayoutList, Calendar as CalIcon, ChevronLeft, ChevronRight,
  AlertCircle, Users as UsersIcon, Flag, TrendingUp, ShieldCheck, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { downloadCsv } from "@/lib/csv";

const PRIORITY_OPTS = [
  { v: "critique", label: "Critique", color: "bg-rose-100 text-rose-700 border-rose-300", dot: "🔴" },
  { v: "elevee",   label: "Élevée",   color: "bg-orange-100 text-orange-700 border-orange-300", dot: "🟠" },
  { v: "moyenne",  label: "Moyenne",  color: "bg-amber-100 text-amber-700 border-amber-300", dot: "🟡" },
  { v: "faible",   label: "Faible",   color: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "🟢" },
];
const priorityMeta = Object.fromEntries(PRIORITY_OPTS.map(p => [p.v, p]));

const NATURE_OPTS = ["Ressources humaines", "Qualité", "Organisation", "Client", "Commercial", "Sécurité", "Formation", "Financier", "Autre"];
const CAUSES_OPTS = ["Manque d'effectif", "Manque de compétences", "Organisation", "Communication", "Process non respecté", "Manque de matériel", "Planning", "Autre"];
const RISKS_OPTS  = ["Manque de personnel", "Budget", "Délais", "Refus du client", "Manque de matériel", "Autre"];
const SUPPORT_OPTS = ["Direction (DG, DOP, DR)", "RH", "Qualité", "Formation (RT, MM)", "Maintenance", "Commerce", "Autre"];

const STATUS_OPTS = [
  { v: "non_demarre", label: "Non démarré", progress: 0 },
  { v: "preparation", label: "En préparation", progress: 25 },
  { v: "en_cours", label: "En cours", progress: 50 },
  { v: "presque_termine", label: "Presque terminé", progress: 75 },
  { v: "termine", label: "Terminé", progress: 100 },
  { v: "cloture", label: "Clôturé", progress: 100 },
];
const CLOSURE_OPTS = [
  { v: "oui", label: "Oui", color: "text-emerald-700 bg-emerald-50 border-emerald-300" },
  { v: "partiel", label: "Partiellement", color: "text-amber-700 bg-amber-50 border-amber-300" },
  { v: "non", label: "Non", color: "text-rose-700 bg-rose-50 border-rose-300" },
];

const EMPTY_FORM = {
  objective: "", responsible_id: "", sector_id: "", corner: "",
  start_date: "", deadline: "",
  context: "", problem_nature: [], problem_nature_other: "", priority: "",
  causes: [], causes_other: "", detailed_plan: "",
  risks: [], risks_other: "", risks_comment: "",
  support_needs: [], support_needs_other: "",
  tracking_status: "non_demarre", tracking_progress: 0,
  results_obtained: "", difficulties: "", corrective_actions: "",
  closure_goal_reached: "", closure_result: "",
  tasks: [],
};

function CheckList({ options, selected, onToggle, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={testid}>
      {options.map(o => (
        <label key={o} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 cursor-pointer text-sm">
          <input type="checkbox" checked={selected.includes(o)} onChange={() => onToggle(o)} className="w-3.5 h-3.5 rounded"/>
          <span className="text-slate-700">{o}</span>
        </label>
      ))}
    </div>
  );
}

function SectionHead({ n, title, Icon }) {
  return (
    <div className="flex items-center gap-2 pb-1 border-b border-slate-200">
      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-xs font-bold">{n}</div>
      <Icon className="w-4 h-4 text-indigo-500"/>
      <div className="font-display text-sm font-semibold text-slate-800 tracking-tight">{title}</div>
    </div>
  );
}

function ProgressBar({ v }) {
  const clr = v >= 100 ? "from-emerald-500 to-emerald-600" : v >= 75 ? "from-blue-500 to-emerald-500" : v >= 50 ? "from-indigo-500 to-blue-500" : v >= 25 ? "from-amber-500 to-indigo-500" : "from-slate-400 to-slate-500";
  return (
    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full bg-gradient-to-r ${clr} transition-all`} style={{ width: `${Math.max(0, Math.min(100, v))}%` }}/>
    </div>
  );
}

function daysBetween(a, b) {
  if (!a || !b) return null;
  const d1 = new Date(a); const d2 = new Date(b);
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
}

export default function Plans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [step, setStep] = useState(0); // 0..6 accordion sections

  const [view, setView] = useState("list");
  const [f, setF] = useState({ q: "", dr: "all", user: "all", sector: "all", status: "all", date_from: "", date_to: "" });
  const [monthOffset, setMonthOffset] = useState(0);

  const load = async () => {
    const [{ data: p }, { data: u }, { data: s }, { data: st }] = await Promise.all([
      api.get("/plans"), api.get("/users"), api.get("/sectors"), api.get("/stores"),
    ]);
    setPlans(p); setUsers(u); setSectors(s); setStores(st);
  };
  useEffect(() => { load(); }, []);

  const drs = useMemo(() => users.filter(u => u.role === "director"), [users]);
  const managers = useMemo(() => users.filter(u => u.role === "manager"), [users]);
  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  // All users, ordered for the "affecté à" dropdown
  const assignableUsers = useMemo(() => {
    const roleOrder = { admin: 0, director: 1, manager: 2, controller: 3, rt: 4, mm: 5 };
    return [...users].sort((a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9) || (a.name || "").localeCompare(b.name || ""));
  }, [users]);

  const filtered = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return plans.filter(p => {
      if (f.sector !== "all" && p.sector_id !== f.sector) return false;
      if (f.user !== "all" && p.responsible_id !== f.user) return false;
      if (f.dr !== "all") {
        const dr = drs.find(d => d.id === f.dr);
        const drSectors = dr?.sectors || [];
        if (!drSectors.includes(p.sector_id)) return false;
      }
      if (f.status !== "all") {
        if (f.status === "in_progress" && p.status === "completed") return false;
        if (f.status === "completed" && p.status !== "completed") return false;
      }
      if (f.date_from && p.deadline && p.deadline < f.date_from) return false;
      if (f.date_to && p.deadline && p.deadline > f.date_to) return false;
      if (!q) return true;
      return [p.objective, p.corner, usersById[p.responsible_id]?.name].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [plans, f, drs, usersById]);

  const toggleArr = (key, val) => setForm(prev => {
    const arr = prev[key] || [];
    return { ...prev, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.objective.trim() || !form.responsible_id) {
      toast.error("Objectif et affectation requis"); return;
    }
    setSaving(true);
    try {
      const status = STATUS_OPTS.find(s => s.v === form.tracking_status);
      const progress = form.tracking_progress != null ? Number(form.tracking_progress) : status?.progress || 0;
      const payload = { ...form, tracking_progress: progress };
      await api.post("/plans", payload);
      toast.success("Plan d'action créé");
      setOpen(false); setForm(EMPTY_FORM); setStep(0);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const loadComments = async (pid) => { try { const { data } = await api.get("/comments", { params: { entity_type: "plan", entity_id: pid } }); setComments({ ...comments, [pid]: data }); } catch {} };
  const toggleExpand = (pid) => { if (expanded === pid) setExpanded(null); else { setExpanded(pid); loadComments(pid); } };
  const submitComment = async (pid) => { if (!newComment.trim()) return; try { await api.post("/comments", { entity_type: "plan", entity_id: pid, text: newComment }); setNewComment(""); loadComments(pid); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const updateStatus = async (plan, status) => {
    try {
      const st = STATUS_OPTS.find(x => x.v === status);
      const { data } = await api.patch(`/plans/${plan.id}`, {
        tracking_status: status, tracking_progress: st?.progress ?? plan.progress
      });
      setPlans(plans.map(p => p.id === plan.id ? data : p));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const userName = (id) => usersById[id]?.name || "—";
  const canCreate = ["director", "admin", "manager"].includes(user?.role);

  const monthStart = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() + monthOffset); d.setDate(1); d.setHours(0,0,0,0); return d; }, [monthOffset]);
  const monthLabel = monthStart.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  const firstDay = (monthStart.getDay() + 6) % 7;
  const grid = [...Array(firstDay).fill(null), ...Array(daysInMonth).fill(0).map((_, i) => i + 1)];
  while (grid.length % 7 !== 0) grid.push(null);
  const plansByDate = useMemo(() => { const m = {}; filtered.forEach(p => { if (p.deadline) (m[p.deadline] = m[p.deadline] || []).push(p); }); return m; }, [filtered]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="plans-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Plans d'action</h1>
          <p className="text-slate-500 mt-1">Objectifs, tâches, échéances et suivi de l'avancement.</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button data-testid="plan-new" onClick={() => { setForm(EMPTY_FORM); setStep(0); setOpen(true); }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm">
              <Plus className="w-4 h-4"/> Nouveau plan
            </button>
            <button data-testid="plan-export" onClick={() => downloadCsv("/export/plans.csv", "plans.csv")}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm">
              <Download className="w-4 h-4"/> CSV
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={f.q} onChange={e => setF({...f, q: e.target.value})} data-testid="plan-search"
                   placeholder="Rechercher un plan…" className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-blue-100"/>
          </div>
          <div className="flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
            {[["list", LayoutList, "Liste"], ["calendar", CalIcon, "Calendrier"]].map(([v, Icon, label]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`plan-view-${v}`}
                      className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${view === v ? "bg-white text-blue-600" : "text-slate-500 hover:text-slate-800"}`}>
                <Icon className="w-3.5 h-3.5"/> {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={f.dr} onChange={e => setF({...f, dr: e.target.value})} data-testid="plan-filter-dr" className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous les DR</option>{drs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={f.user} onChange={e => setF({...f, user: e.target.value})} data-testid="plan-filter-user" className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous responsables</option>{assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={f.sector} onChange={e => setF({...f, sector: e.target.value})} data-testid="plan-filter-sector" className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous secteurs</option>{sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={f.status} onChange={e => setF({...f, status: e.target.value})} data-testid="plan-filter-status" className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs">
            <option value="all">Tous statuts</option><option value="in_progress">En cours</option><option value="completed">Clôturés</option>
          </select>
          <input type="date" value={f.date_from} onChange={e => setF({...f, date_from: e.target.value})} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs"/>
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={f.date_to} onChange={e => setF({...f, date_to: e.target.value})} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs"/>
          <span className="ml-auto text-xs text-slate-500 font-semibold">{filtered.length} plan(s)</span>
        </div>
      </div>

      {view === "list" && (
        <div className="space-y-4">
          {filtered.map((p) => {
            const pm = priorityMeta[p.priority];
            const stObj = STATUS_OPTS.find(s => s.v === (p.tracking_status || "non_demarre"));
            const daysLeft = daysBetween(today, p.deadline);
            const daysPlanned = daysBetween(p.start_date, p.deadline);
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className="bg-white border border-slate-200 rounded-2xl p-6 card-hover">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 grid place-items-center text-indigo-600 shrink-0"><Target className="w-5 h-5"/></div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {pm && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${pm.color}`}>{pm.dot} {pm.label.toUpperCase()}</span>}
                        {p.corner && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">🏪 {p.corner}</span>}
                        {stObj && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">{stObj.label}</span>}
                      </div>
                      <div className="font-display font-semibold text-lg text-slate-900 mt-1">{p.objective}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Affecté à : <b>{userName(p.responsible_id)}</b>
                        {p.deadline && ` — Échéance ${new Date(p.deadline).toLocaleDateString("fr-FR")}`}
                        {daysLeft != null && p.deadline && (
                          <span className={`ml-1 font-semibold ${daysLeft < 0 ? "text-rose-600" : daysLeft <= 7 ? "text-amber-600" : "text-emerald-600"}`}>
                            ({daysLeft < 0 ? `en retard de ${-daysLeft}j` : `${daysLeft}j restant${daysLeft > 1 ? "s" : ""}`}
                            {daysPlanned ? ` · durée ${daysPlanned}j` : ""})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-bold text-indigo-600">{p.progress || 0}%</div>
                    <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">Avancement</div>
                  </div>
                </div>
                <div className="mt-4"><ProgressBar v={p.progress || 0}/></div>

                {(p.context || (p.problem_nature || []).length > 0) && (
                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    {p.context && <div><span className="font-bold uppercase tracking-wider text-slate-500 text-[10px]">Constat</span><div className="text-slate-700 mt-0.5">{p.context}</div></div>}
                    {(p.problem_nature || []).length > 0 && <div><span className="font-bold uppercase tracking-wider text-slate-500 text-[10px]">Nature</span><div className="text-slate-700 mt-0.5">{p.problem_nature.join(" · ")}</div></div>}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Statut :</span>
                  <select data-testid={`plan-status-${p.id}`} value={p.tracking_status || "non_demarre"} onChange={e => updateStatus(p, e.target.value)}
                          className="px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs font-medium">
                    {STATUS_OPTS.map(s => <option key={s.v} value={s.v}>{s.label} — {s.progress}%</option>)}
                  </select>
                  <button data-testid={`plan-comments-${p.id}`} onClick={() => toggleExpand(p.id)}
                          className="ml-auto text-sm text-indigo-600 flex items-center gap-2 hover:underline">
                    <MessageCircle className="w-4 h-4"/> Commentaires
                    {expanded === p.id ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
                  </button>
                </div>

                {expanded === p.id && (
                  <div className="mt-3 space-y-3">
                    {(p.detailed_plan || p.results_obtained || p.difficulties || p.corrective_actions || p.closure_result) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        {p.detailed_plan && <div className="bg-slate-50 rounded-lg p-3"><div className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Plan détaillé</div><div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{p.detailed_plan}</div></div>}
                        {p.results_obtained && <div className="bg-emerald-50 rounded-lg p-3"><div className="font-bold text-emerald-700 uppercase tracking-wider text-[10px]">Résultats obtenus</div><div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{p.results_obtained}</div></div>}
                        {p.difficulties && <div className="bg-rose-50 rounded-lg p-3"><div className="font-bold text-rose-700 uppercase tracking-wider text-[10px]">Difficultés</div><div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{p.difficulties}</div></div>}
                        {p.corrective_actions && <div className="bg-amber-50 rounded-lg p-3"><div className="font-bold text-amber-700 uppercase tracking-wider text-[10px]">Actions correctives</div><div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{p.corrective_actions}</div></div>}
                        {p.closure_result && <div className="bg-indigo-50 rounded-lg p-3 sm:col-span-2"><div className="font-bold text-indigo-700 uppercase tracking-wider text-[10px]">Résultat final ({p.closure_goal_reached || "—"})</div><div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{p.closure_result}</div></div>}
                      </div>
                    )}
                    {(comments[p.id] || []).map((c) => (
                      <div key={c.id} className="text-sm bg-slate-50 rounded-xl px-4 py-3">
                        <div className="font-semibold text-slate-900">{c.user_name} <span className="text-xs text-slate-400 font-normal">— {new Date(c.created_at).toLocaleString("fr-FR")}</span></div>
                        <div className="text-slate-600 mt-1">{c.text}</div>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input value={newComment} onChange={(e)=>setNewComment(e.target.value)} placeholder="Ajouter un commentaire…" className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                      <button onClick={() => submitComment(p.id)} className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold">Envoyer</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (<div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-400">Aucun plan d'action.</div>)}
        </div>
      )}

      {view === "calendar" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="plan-cal-prev" onClick={() => setMonthOffset(m => m - 1)} className="w-9 h-9 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4"/></button>
            <div className="font-display text-lg font-semibold text-slate-800 capitalize">{monthLabel}</div>
            <button data-testid="plan-cal-next" onClick={() => setMonthOffset(m => m + 1)} className="w-9 h-9 grid place-items-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight className="w-4 h-4"/></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs">
            {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (<div key={d} className="p-2 text-center font-semibold text-slate-400 uppercase">{d}</div>))}
            {grid.map((day, i) => {
              if (day == null) return <div key={i}/>;
              const iso = new Date(monthStart.getFullYear(), monthStart.getMonth(), day).toISOString().slice(0,10);
              const evs = plansByDate[iso] || [];
              const isToday = iso === today;
              return (
                <div key={i} className={`min-h-[80px] p-1.5 rounded-lg border ${isToday ? "bg-blue-50 border-blue-300" : "bg-slate-50 border-slate-100"}`}>
                  <div className={`text-[11px] font-bold ${isToday ? "text-blue-600" : "text-slate-500"}`}>{day}</div>
                  <div className="mt-1 space-y-0.5">
                    {evs.slice(0, 3).map(e => (
                      <div key={e.id} className="text-[10px] px-1.5 py-0.5 rounded-md truncate font-semibold bg-indigo-100 text-indigo-700">{e.objective.slice(0, 22)}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                className="bg-white rounded-2xl w-full max-w-3xl border border-slate-200 shadow-xl max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Target className="w-5 h-5 text-indigo-600"/> Nouveau plan d'action</h2>
              <button type="button" onClick={()=>setOpen(false)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto scrollbar-thin flex-1 min-h-0">

              {/* 1 · Contexte */}
              <div className="space-y-3">
                <SectionHead n={1} title="Le Contexte" Icon={AlertCircle}/>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Quel est le constat ?</label>
                  <textarea data-testid="plan-context" rows={3} value={form.context} onChange={e => setForm({...form, context: e.target.value})}
                            placeholder="Baisse de qualité, difficultés RH, taux d'absentéisme, réclamations clients, organisation, retards, non-conformités…"
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Nature de la problématique</label>
                  <CheckList options={NATURE_OPTS} selected={form.problem_nature} onToggle={v => toggleArr("problem_nature", v)} testid="plan-nature"/>
                  {form.problem_nature.includes("Autre") && (
                    <input data-testid="plan-nature-other" value={form.problem_nature_other} onChange={e => setForm({...form, problem_nature_other: e.target.value})}
                           placeholder="Précisez la nature…" className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Niveau de priorité</label>
                    <div className="flex gap-1.5 flex-wrap" data-testid="plan-priority">
                      {PRIORITY_OPTS.map(p => (
                        <button type="button" key={p.v} onClick={() => setForm({...form, priority: p.v})}
                                className={`text-xs px-2.5 py-1.5 rounded-lg border font-semibold ${form.priority === p.v ? p.color + " ring-2 ring-offset-1 ring-indigo-300" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                          {p.dot} {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Corner concerné</label>
                    <select data-testid="plan-corner" value={form.corner} onChange={e => setForm({...form, corner: e.target.value})}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                      <option value="">— Aucun / Général —</option>
                      {stores.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* 2 · Objectif recherché */}
              <div className="space-y-3">
                <SectionHead n={2} title="Objectif recherché" Icon={Target}/>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Quel est l'objectif ? *</label>
                  <textarea data-testid="plan-objective" required rows={2} value={form.objective} onChange={e => setForm({...form, objective: e.target.value})}
                            placeholder="Décrivez clairement l'objectif à atteindre…"
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Affecté à *</label>
                  <select data-testid="plan-responsible" required value={form.responsible_id} onChange={e => setForm({...form, responsible_id: e.target.value})}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                    <option value="">— Sélectionner un utilisateur —</option>
                    {assignableUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.name} · {(u.role || "").toUpperCase()}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 3 · Analyse des causes */}
              <div className="space-y-3">
                <SectionHead n={3} title="Analyse des causes" Icon={Flag}/>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Cause(s) identifiée(s)</label>
                  <CheckList options={CAUSES_OPTS} selected={form.causes} onToggle={v => toggleArr("causes", v)} testid="plan-causes"/>
                  {form.causes.includes("Autre") && (
                    <input data-testid="plan-causes-other" value={form.causes_other} onChange={e => setForm({...form, causes_other: e.target.value})}
                           placeholder="Précisez la cause…" className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Votre plan détaillé</label>
                  <textarea data-testid="plan-detailed" rows={4} value={form.detailed_plan} onChange={e => setForm({...form, detailed_plan: e.target.value})}
                            placeholder="Étape par étape, décrivez le plan d'attaque…"
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
              </div>

              {/* 4 · Risques */}
              <div className="space-y-3">
                <SectionHead n={4} title="Risques" Icon={AlertCircle}/>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Quels sont les risques pouvant empêcher la réussite ?</label>
                  <CheckList options={RISKS_OPTS} selected={form.risks} onToggle={v => toggleArr("risks", v)} testid="plan-risks"/>
                  {form.risks.includes("Autre") && (
                    <input data-testid="plan-risks-other" value={form.risks_other} onChange={e => setForm({...form, risks_other: e.target.value})}
                           placeholder="Précisez le risque…" className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Commentaires</label>
                  <textarea data-testid="plan-risks-comment" rows={2} value={form.risks_comment} onChange={e => setForm({...form, risks_comment: e.target.value})}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
              </div>

              {/* 5 · Accompagnement */}
              <div className="space-y-3">
                <SectionHead n={5} title="Besoins d'accompagnement" Icon={UsersIcon}/>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Avez-vous besoin d'un soutien ?</label>
                  <CheckList options={SUPPORT_OPTS} selected={form.support_needs} onToggle={v => toggleArr("support_needs", v)} testid="plan-support"/>
                  {form.support_needs.includes("Autre") && (
                    <input data-testid="plan-support-other" value={form.support_needs_other} onChange={e => setForm({...form, support_needs_other: e.target.value})}
                           placeholder="Précisez le soutien…" className="mt-2 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Date de début</label>
                    <input data-testid="plan-start" type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Date estimée de fin</label>
                    <input data-testid="plan-deadline" type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  </div>
                </div>
                {form.start_date && form.deadline && (
                  <div className="text-xs text-slate-500">
                    Durée estimée : <b>{daysBetween(form.start_date, form.deadline)} jours</b>
                    {daysBetween(today, form.deadline) != null && (
                      <span className="ml-2">· Échéance dans <b>{daysBetween(today, form.deadline)} jour(s)</b></span>
                    )}
                  </div>
                )}
              </div>

              {/* 6 · Suivi d'avancement */}
              <div className="space-y-3">
                <SectionHead n={6} title="Suivi d'avancement" Icon={TrendingUp}/>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">État actuel</label>
                    <select data-testid="plan-tracking-status" value={form.tracking_status} onChange={e => {
                      const s = STATUS_OPTS.find(x => x.v === e.target.value);
                      setForm({...form, tracking_status: e.target.value, tracking_progress: s?.progress ?? form.tracking_progress });
                    }} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
                      {STATUS_OPTS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600 mb-1 block">Avancement : <b>{form.tracking_progress || 0}%</b></label>
                    <input data-testid="plan-tracking-progress" type="range" min="0" max="100" step="25" value={form.tracking_progress}
                           onChange={e => setForm({...form, tracking_progress: Number(e.target.value)})}
                           className="w-full accent-indigo-600"/>
                    <ProgressBar v={form.tracking_progress}/>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Résultats déjà obtenus</label>
                  <textarea data-testid="plan-results" rows={2} value={form.results_obtained} onChange={e => setForm({...form, results_obtained: e.target.value})}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Difficultés rencontrées</label>
                  <textarea data-testid="plan-difficulties" rows={2} value={form.difficulties} onChange={e => setForm({...form, difficulties: e.target.value})}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Actions correctives si nécessaire</label>
                  <textarea data-testid="plan-corrective" rows={2} value={form.corrective_actions} onChange={e => setForm({...form, corrective_actions: e.target.value})}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
              </div>

              {/* 7 · Clôture */}
              <div className="space-y-3">
                <SectionHead n={7} title="Clôture du plan" Icon={ShieldCheck}/>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Objectif atteint ?</label>
                  <div className="flex gap-1.5 flex-wrap" data-testid="plan-closure">
                    {CLOSURE_OPTS.map(c => (
                      <button type="button" key={c.v} onClick={() => setForm({...form, closure_goal_reached: c.v})}
                              className={`text-xs px-3 py-1.5 rounded-lg border font-semibold ${form.closure_goal_reached === c.v ? c.color + " ring-2 ring-offset-1 ring-indigo-300" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Résultat final</label>
                  <textarea data-testid="plan-closure-result" rows={2} value={form.closure_result} onChange={e => setForm({...form, closure_result: e.target.value})}
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <div className="text-xs text-slate-500">Champs marqués <b>*</b> obligatoires — Objectif et Affecté à.</div>
              <div className="flex gap-2">
                <button type="button" onClick={()=>setOpen(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
                <button data-testid="plan-submit" type="submit" disabled={saving} className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60 flex items-center gap-1">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>} Créer le plan
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
